import "ts-error-as-value/lib/globals";
import { PostgrestError, PostgrestSingleResponse } from "@supabase/supabase-js";
import { PostgrestResponseFailure } from "@supabase/postgrest-js/src/types";
import { isType1 } from "#/utils/general";


export interface ISupabaseError extends PostgrestError {
  statusText: string | null,
  name: "PostgrestError",
  stack?: string,
  parentStack?: string | null
}

interface SupabaseErrorObj {
  name: "PostgrestError",
  message: string,
  details: string,
  hint: string,
  code: string,
  statusText: string,
  stack: string
}

export class SupabaseError extends Error implements ISupabaseError {
  public name: "PostgrestError" = "PostgrestError";
  public message: string = "No message";
  public stack?: string;
  constructor(
    message: string,
    public parentStack: string | undefined | null,
    public details: string = "",
    public hint: string = "",
    public code: string = "",
    public statusText: string | null = ""
  ) {
    super(message);
    this.message = message;
    this.stack = super.stack;
    if (parentStack) {
      const parentStackWithoutFirstLine = parentStack.split("\n").slice(1).join("\n");
      this.stack = `${this.name}: ${message}\n${parentStackWithoutFirstLine}`.trim();
    }
  }
  static fromPostgrestError(
    error: PostgrestError,
    statusText: string | null = null,
    parentStack: string | undefined
  ): SupabaseError {
    const message = error.message ?? "No message";
    const _error = new SupabaseError(
      message,
      parentStack,
      error.details,
      error.hint,
      error.code,
      statusText
    );
    return _error;
  }

  static fromSupabaseResult(
    failure: PostgrestResponseFailure,
    parentStack?: string
  ): SupabaseError {
    return SupabaseError.fromPostgrestError(
      failure.error,
      failure.statusText,
      parentStack
    );
  }
  toObject(): SupabaseErrorObj {
    if (!this.stack) {
      throw new Error("Stack is not defined, this should not happen.");
    }
    return {
      message: this.message,
      details: this.details,
      hint: this.hint,
      code: this.code,
      statusText: this.statusText ?? "",
      name: this.name,
      stack: this.stack
    };
  }

  serialize(): string {
    return JSON.stringify(this.toObject());
  }
}
/**
 *  Supabase PostgrestErrors do not have a name or stacktrace, making them hard to debug.
 *  This functions makes it easy to add a .then(supabaseThen) after a supabase query to get a stacktrace.
 */
export const supabaseThen = <T extends string>(
  parentStack: T extends "" ? never : T | undefined
) => <T>(
  result: PostgrestSingleResponse<T>
): PostgrestSingleResponse<T> => {
  if (result.error == null) {
    return result;
  }
  const error = SupabaseError.fromSupabaseResult(result, parentStack).toObject();
  return {
    ...result,
    data: null,
    error
  } as PostgrestResponseFailure;
};

export const supabaseThenResult = <T extends string>(
  parentStack: T extends "" ? never : T | undefined
) => <T>(
  result: PostgrestSingleResponse<T> | Result<T>
): Result<T, SupabaseError> => {
  if (result.error == null) {
    return ok(result.data);
  }
  if (isType1<Result<T>, PostgrestSingleResponse<T>>(result, result.error instanceof Error)) {
    return err(new SupabaseError(result.error.message, parentStack ?? null, result.error?.cause?.toString() ?? ""));
  }
  return err(SupabaseError.fromSupabaseResult(result, parentStack));
};

export const supabaseStorageThenResult = <T extends string>(
  parentStack: T extends "" ? never : T | undefined
) => <R, E extends Error>(
  result: { data: null, error: E } | { data: R, error: null }
): Result<NonNullable<R>, SupabaseError> => {
  if (result.error == null) {
    if (result.data == null) {
      return err(new SupabaseError(
        "Both data and error are null", parentStack
      ));
    }
    return ok(result.data);
  }
  if (isType1<{ data: null, error: E } | { data: R, error: null }, PostgrestSingleResponse<R>>(result, (result.error as any) instanceof Error)) {
    return err(new SupabaseError(result.error.message, parentStack ?? null, result.error?.cause?.toString() ?? ""));
  }
  return err(SupabaseError.fromSupabaseResult(result, parentStack));
};